import {ChangeDetectorRef, Component, ComponentFactoryResolver, ViewEncapsulation,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FieldRecord} from "../../../model/api/field-record";
import {FieldRequest} from "../../../model/api/field-request";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {CrudBaseComponent} from "../../../crystal-elements/crud/crud-base.component";
import {CustomerLimits} from "../../../model/customer/customer-limits";
import {Customer} from "../../../model/customer/customer";
import {ExchangeDetail} from "../../../model/exchange/exchange-detail";
import {AccountDefinition} from "../../../model/customer/account-definition";
import {CustomerCustomerLimitsService} from "../../../services/customer/customer-customer-limits.service";
import {ContractLight} from "../../../model/exchange/contract-light";
import TYPE = FormFieldConfig.TYPE;

@Component({
  selector: 'app-cust-limits',
  templateUrl: './customer-limits.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class CustomerLimitsComponent extends CrudBaseComponent<CustomerLimits> {

  //fields
  owners: FieldRecord<any>[];

  customers: FieldRecord<Customer>[];
  displayedCustomers: FieldRecord<Customer>[];

  clearingHouses: FieldRecord<any>[];

  exchangeCodes: FieldRecord<ExchangeDetail>[];
  displayedExchangeCodes: FieldRecord<any>[];

  contracts: FieldRecord<ContractLight>[] = [];
  currentContractsRequest: FieldRequest;
  // displayedContracts: FieldRecord<ContractLight>[]

  accounts: FieldRecord<AccountDefinition>[];
  displayedAccounts: FieldRecord<AccountDefinition>[];

  constructor(cdref: ChangeDetectorRef,
              customerCustomerLimitsService: CustomerCustomerLimitsService,
              commonFieldService: CommonFieldService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.crudService = customerCustomerLimitsService;
    this.newRecord = new CustomerLimits();
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'owner',
      },
      {
        field: 'clearingHouse',
      },
      {
        field: 'exchangeCode',
        icon: (record: AccountDefinition) => {
          if (this.exchangeCodes) {
            const exchField = this.exchangeCodes.find(exchange => exchange.value.code === record.exchangeCode);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
      },
      {
        field: 'contractCode',
      },
      {
        field: 'customerId',
        mapId: record => {
          if (this.customers) {
            let foundCustomer = this.customers.find(customer => customer.id === record.customerId + '')
            if (foundCustomer) {
              return foundCustomer.text;
            }
          }
          return '';
        }
      },
      {
        field: 'accountCode',
      },
      {
        field: 'positionLimit',
      },
      {
        field: 'volumeLimit',
      },
    ];

    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'owner',
            optionValue: 'text',
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.owners,
            changed: event => {
              this.formRecord.owner = event.value;
              this.formRecord.customerId = undefined;
              this.formRecord.customerName = undefined;
              this.formRecord.accountCode = undefined;
              this.filterCustomers();
              this.filterAccounts();
            }
          },
          {
            field: 'customerId',
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.displayedCustomers,
            changed: event => {
              this.formRecord.customerId = event.value;
              this.formRecord.customerName = this.customers.find(value => value.id === event.value).text;
              this.formRecord.accountCode = undefined;
              this.filterAccounts();
            }
          },
          {
            field: 'clearingHouse',
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.clearingHouses,
            changed: event => {
              this.formRecord.clearingHouse = event.value;
              this.formRecord.exchangeCode = undefined;
              this.formRecord.contractCode = undefined;
              this.formRecord.accountCode = undefined;
              this.filterExchanges();
              this.filterAccounts();
              this.filterContracts();
            }
          },
          {
            field: 'exchangeCode',
            icon: (id: string) => {
              if (this.exchangeCodes) {
                const exchField = this.exchangeCodes.find(exchange => exchange.value.code === id);
                if (exchField && exchField.value)
                  return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
              }
              return 'fas fa-globe-europe';
            },
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.displayedExchangeCodes,
            changed: event => {
              this.formRecord.exchangeCode = event.value;
              this.formRecord.contractCode = undefined;
              this.formRecord.accountCode = undefined;
              this.filterAccounts();
              this.filterContracts();
            }
          },
          {
            field: 'contractCode',
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            optionValue: 'text',
            icon: (id: string) => {
              const contractField = this.contracts.find(field => field.id === id);
              if (contractField) {
                if (contractField.id.endsWith('-F'))
                  return 'cc-ico cc-ico-future';
                else if (contractField.id.endsWith('-O'))
                  return 'cc-ico cc-ico-option';
                else if (contractField.id.endsWith('-S'))
                  return 'cc-ico cc-ico-stock';
              }
              return '';
            },
            options: () => this.contracts,
            changed: event => {
              const contract = this.contracts.find(field => field.value.code == this.formRecord.contractCode);
              if (contract) {
                this.formRecord.contractCode = contract.value.code;
                if (this.formRecord.contractCode) {
                  if (contract) {
                    this.formRecord.contractVersion = contract.value.version;
                  }
                }
              }
            }
          },
          {
            field: 'accountCode',
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.displayedAccounts,
          },
          {
            field: 'positionLimit',
            column: 4,
            type: TYPE.INTEGER
          },
          {
            field: 'volumeLimit',
            column: 4,
            type: TYPE.INTEGER
          },
          {
            field: 'contractVersion',
            column: 4,
            type: TYPE.TEXT,
            disabled: record => true
          }]
      },
    ];
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    this.commonFieldService.getCustomers(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.customers = this.getFields(fieldResult);
      this.filterCustomers();
      this.filterAccounts();
      this.settingsFormLoadingProgressValue += 100 / 6;
    })
    this.commonFieldService.getOwners(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.owners = this.getFields(fieldResult);
      this.filterCustomers();
      this.filterAccounts();
      this.settingsFormLoadingProgressValue += 100 / 6;
    })
    this.commonFieldService.getClearingHouses(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.clearingHouses = this.getFields(fieldResult);
      this.filterExchanges();
      this.filterAccounts();
      this.filterContracts();
      this.settingsFormLoadingProgressValue += 100 / 6;
    })
    this.commonFieldService.getExchangeCodes(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.exchangeCodes = this.getFields(fieldResult);
      this.filterExchanges();
      this.filterAccounts();
      this.filterContracts();
      this.settingsFormLoadingProgressValue += 100 / 6;
    });
    this.commonFieldService.getAccounts(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.accounts = this.getFields(fieldResult);
      this.filterAccounts();
      this.settingsFormLoadingProgressValue += 100 / 6;
    });
    // this.commonFieldService.getContracts(FieldRequest.DEFAULT()).subscribe(fieldResult => {
    //   this.contracts = this.getFields(fieldResult);
    //   this.filterContracts();
    //   this.loadingProgressValue += 100 / 6;
    // });
  }

  private filterExchanges() {
    // let defaultAllExchange = FieldRecord.DEFAULT();
    // defaultAllExchange.text = this.translateService.instant('page.account.form.defaultExchangeAll');
    // defaultAllExchange.id = ExchangeDetail.ALL;
    if (this.exchangeCodes && this.formRecord) {
      this.displayedExchangeCodes = this.exchangeCodes.filter(field => this.formRecord.clearingHouse === field.value.clearingHouse);
      // this.displayedExchangeCodes.unshift(defaultAllExchange);
    } else {
      this.displayedExchangeCodes = [];
    }
  }

  private filterAccounts() {
    if (this.accounts && this.formRecord) {
      this.displayedAccounts = this.accounts.filter(field =>
        field.value.customerId == this.formRecord.customerId
        && this.formRecord.clearingHouse === field.value.clearingHouse
        && (!field.value.exchangeCode || field.value.exchangeCode.length === 0
          || field.value.exchangeCode === ExchangeDetail.ALL
          || field.value.exchangeCode === ExchangeDetail.ALL_TEXT
          || this.formRecord.exchangeCode === field.value.exchangeCode));

    } else {
      this.displayedAccounts = [];
    }
  }

  private filterContracts() {
    // if (this.contracts && this.formRecord) {
    //   console.log("----> accs "+JSON.stringify(this.accounts));
    //   this.displayedContracts = this.contracts.filter(field =>
    //     !field.value.exchangeCode || field.value.exchangeCode === ExchangeDetail.ALL || this.formRecord.exchangeCode === field.value.exchangeCode);
    // } else {
    //   this.displayedContracts = [];
    // }
    if (this.formRecord && this.formRecord.exchangeCode) {
      const newRequest = new FieldRequest(this.formRecord.clearingHouse, this.formRecord.exchangeCode);
      if (!this.currentContractsRequest || this.currentContractsRequest.search != newRequest.search || this.currentContractsRequest.search2 != newRequest.search2) {
        this.formLoading = true;
        this.currentContractsRequest = newRequest;
        this.commonFieldService.getContracts(this.currentContractsRequest).subscribe(fieldResult => {
            const contractsFields = this.getFields(fieldResult);

            this.contracts = contractsFields ? contractsFields.filter(field => field.value.id.endsWith('-F')) : [];
            if (this.settingsFormLoadingProgressValue < 100) {
              this.settingsFormLoadingProgressValue += 100 / 6;
            }
            this.formLoading = false;
          }
        );
      }
    } else {
      if (this.settingsFormLoadingProgressValue < 100) {
        this.settingsFormLoadingProgressValue += 100 / 6;
      }
    }
  }


  private filterCustomers() {
    if (this.customers && this.formRecord) {
      this.displayedCustomers = this.customers.filter(field => {
        return this.formRecord.owner === field.value.owner;
      });
    } else {
      this.displayedCustomers = [];
    }
  }


  onFormShow() {
    this.filterCustomers();
    this.filterExchanges();
    this.filterAccounts();
    this.filterContracts();
  }
}
